/**
 * Video Service - Serviço para gerenciamento de vídeos
 *
 * Fornece métodos para buscar, criar, atualizar e deletar vídeos
 */

import { api } from './api';
import type { ApiActionButton } from '@/lib/action-button/api-mapping';

export interface VideoTracking {
  conversions: number;
  revenue: number;
  currency: 'BRL' | 'USD';
}

export interface Video {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  duration: number;
  status: 'pending' | 'processing' | 'partial' | 'active' | 'error';
  createdAt: string;
  updatedAt?: string;
  plays: number;
  views: number;
  videoDownload?: string | null;
  tracking?: VideoTracking | null;
  sharedId?: string; // ID do vídeo original; se diferente de id, é vídeo recebido via compartilhamento
}

export interface VideosListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'pending' | 'processing' | 'partial' | 'active' | 'error';
  sortBy?: 'createdAt' | 'title' | 'plays';
  sortOrder?: 'ASC' | 'DESC';
  includeFolderVideos?: boolean;
}

export interface VideosListResponse {
  videos: Video[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export const videoService = {
  duplicateVideo: async (videoId: string): Promise<{ id_video: string }> => {
    const response = await api.post<{ success: boolean; data: { id_video: string } }>(
      `/videos/duplicate/${encodeURIComponent(videoId)}`,
    );
    return response.data;
  },

  /**
   * GET /videos/dashboard/list
   * Lista otimizada de vídeos para o dashboard
   * Com paginação, filtros e métricas agregadas
   */
  getVideosList: async (params: VideosListParams = {}): Promise<VideosListResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.includeFolderVideos) queryParams.append('includeFolderVideos', 'true');

    const response = await api.get<VideosListResponse>(
      `/videos/dashboard/list?${queryParams.toString()}`
    );

    const result = (response as any).data || response;

    // Normaliza sharedId — o backend pode retornar snake_case (shared_id) ou camelCase (sharedId)
    if (result?.videos && Array.isArray(result.videos)) {
      result.videos = result.videos.map((v: any) => ({
        ...v,
        sharedId: v.sharedId || v.shared_id || v.id_shared || undefined,
      }));
    }

    return result;
  },

  /**
   * GET /videos/mongo/get-by-id/:id_video
   * Busca um vídeo específico por ID no MongoDB (mesma fonte que o player lê),
   * eliminando a divergência editor-vs-player. Envelope idêntico ao endpoint
   * MySQL antigo: { response: <vídeo + mapedVideoTimes> }.
   */
  getVideoById: async (videoId: string): Promise<any> => {
    return await api.get(`/videos/mongo/get-by-id/${videoId}`);
  },

  /**
   * GET /videos/get/:id_user
   * Busca todos os vídeos de um usuário específico
   */
  getVideosByUserId: async (userId: string): Promise<any> => {
    return await api.get(`/videos/get/${userId}`);
  },

  /**
   * GET /videos/get/:id_user
   * Busca vídeos do usuário em formato simples
   */
  getVideosByUser: async (userId: string): Promise<any[]> => {
    try {
      const response = await api.get(`/videos/get/${userId}`);

      let videos: any[] = [];

      if (Array.isArray(response)) {
        videos = response;
      } else if (response?.data && Array.isArray(response.data)) {
        videos = response.data;
      }

      return videos;
    } catch (error) {
      console.error('Erro ao buscar vídeos do usuário:', error);
      return [];
    }
  },

  /**
   * GET /videos/dashboard/:userId
   * Busca vídeos do dashboard do usuário para seleção em modais
   */
  getDashboardVideos: async (userId: string): Promise<any[]> => {
    try {
      const response = await api.get(`/videos/dashboard/${userId}`);

      let videos: any[] = [];

      // Se a resposta for um objeto com propriedade 'videos' ou 'data'
      if (response && typeof response === 'object') {
        if (Array.isArray(response.videos)) {
          videos = response.videos;
        } else if (Array.isArray(response.data)) {
          videos = response.data;
        } else if (Array.isArray(response)) {
          videos = response;
        }
      } else if (Array.isArray(response)) {
        videos = response;
      }

      // Mapeia os vídeos para o formato esperado
      const mappedVideos = videos.map((video: any, index: number) => ({
        id: video.id_video || video.id || video._id || video.video_id || video.videoId || `temp-${index}`,
        title: video.title || video.nomeVideo || video.name || 'Sem título',
        thumbnailUrl: video.frame || video.thumbnailUrl || video.thumbnail_url || video.thumbnail || video.imagemPause || video.imagemInicial || video.imagemFinal || video.imageUrl,
        duration: video.duration || video.duracao || 0,
        status: video.status || 'active',
        createdAt: video.createdAt || video.created_at || video.data_criacao || new Date().toISOString(),
        plays: parseInt(video.plays) || parseInt(video.visualizacoes) || parseInt(video.totalPlays) || parseInt(video.total_plays) || 0,
        views: parseInt(video.totalViews) || parseInt(video.views) || parseInt(video.visualizacoes) || 0,
        is_testab: video.is_testab,
        is_lead_test: video.is_lead_test,
      }));

      return mappedVideos;
    } catch (error) {
      console.error('videoService.getDashboardVideos: Erro ao buscar vídeos do dashboard:', error);
      return [];
    }
  },

  /**
   * DELETE /videos/delete/:id_video
   * Deleta um vídeo
   */
  deleteVideo: async (videoId: string): Promise<void> => {
    await api.delete(`/videos/delete/${videoId}`);
  },

  /**
   * PATCH /videos/upload/:id_video
   * Atualiza informações do vídeo
   */
  updateVideo: async (videoId: string, data: any): Promise<any> => {
    return await api.patch(`/videos/upload/${videoId}`, data);
  },

  /**
   * PATCH /videos/responsive-config/:id_video
   * Salva configuração de exibição por dispositivo
   */
  updateResponsiveConfig: async (videoId: string, responsiveConfig: any): Promise<any> => {
    return await api.patch(`/videos/responsive-config/${videoId}`, { responsive_config: responsiveConfig });
  },

  /**
   * PATCH /videos/turbo/:id_video
   * Cria/atualiza teste de velocidade/turbo
   */
  updateSpeedTest: async (videoId: string, data: {
    haveTurbo: boolean;
    turboVelocity?: number;
    haveTurboTest?: boolean;
    turboVelocityRangeBegin?: number;
    turboVelocityRangeEnd?: number;
    is_speed_test?: boolean;
  }): Promise<any> => {
    return await api.patch(`/videos/turbo/${videoId}`, data);
  },

  /**
   * PATCH /videos/upload-logo-image/:id_video
   * Atualiza configurações de logomarca (com upload de imagem)
   */
  updateLogo: async (videoId: string, formData: FormData): Promise<any> => {
    return await api.patch(`/videos/upload-logo-image/${videoId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * DELETE /videos/upload-logo-image/:id_video
   * Remove logomarca
   */
  removeLogo: async (videoId: string): Promise<any> => {
    return await api.delete(`/videos/upload-logo-image/${videoId}`);
  },

  /**
   * DELETE /videos/upload-image/:id_video
   * DELETE /videos/upload-image-inicial/:id_video
   * DELETE /videos/upload-image-final/:id_video
   * Remove thumbnails
   */
  removeThumbnailPause: async (videoId: string): Promise<any> => {
    return await api.delete(`/videos/upload-image/${videoId}`);
  },

  removeThumbnailStart: async (videoId: string): Promise<any> => {
    return await api.delete(`/videos/upload-image-inicial/${videoId}`);
  },

  removeThumbnailEnd: async (videoId: string): Promise<any> => {
    return await api.delete(`/videos/upload-image-final/${videoId}`);
  },

  /**
   * PATCH /videos/upload-image/:id_video (pause)
   * PATCH /videos/upload-image-inicial/:id_video (início)
   * PATCH /videos/upload-image-final/:id_video (final)
   * Atualiza thumbnail com upload de imagem
   */
  updateThumbnailPause: async (videoId: string, formData: FormData): Promise<any> => {
    return await api.patch(`/videos/upload-image/${videoId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  updateThumbnailStart: async (videoId: string, formData: FormData): Promise<any> => {
    return await api.patch(`/videos/upload-image-inicial/${videoId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  updateThumbnailEnd: async (videoId: string, formData: FormData): Promise<any> => {
    return await api.patch(`/videos/upload-image-final/${videoId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * PATCH /videos/upload-image-custom-auto/:id_video
   * Faz upload da imagem custom do autoplay overlay (binário → Bunny CDN).
   * O campo "customImage" do tb_video passa a guardar a URL final.
   */
  updateAutoplayCustomImage: async (
    videoId: string,
    formData: FormData,
  ): Promise<{ success: boolean; mensagem: string; customImage?: string }> => {
    return await api.patch(`/videos/upload-image-custom-auto/${videoId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * PUT /videos/:id/action-buttons — substitui a lista inteira de botões de ação (até 5).
   * Recusa com 422 e `message` em pt-BR.
   */
  saveActionButtons: async (
    videoId: string,
    actionButtons: ApiActionButton[],
  ): Promise<{ actionButtons: ApiActionButton[] }> => {
    return await api.put(`/videos/${videoId}/action-buttons`, { actionButtons });
  },

  /**
   * POST /videos/copy-configurations
   * Copia configurações de personalização de um vídeo para outros vídeos
   */
  copyConfigurations: async (sourceVideoId: string, targetVideoIds: string[]): Promise<{
    success: boolean;
    message: string;
    data?: {
      sourceVideoId: string;
      updatedVideoIds: string[];
      notFoundIds?: string[];
      copiedFields: string[];
    };
  }> => {
    const response = await api.post('/videos/copy-configurations', {
      sourceVideoId,
      targetVideoIds,
    });
    return response;
  },

  /**
   * PATCH /videos/upload/:id_video
   * Salva todas as configurações de personalização do vídeo
   */
  saveMiniHooks: async (videoId: string, miniHooks: any): Promise<any> => {
    return await api.patch(`/videos/mini-hooks/${videoId}`, { miniHooks });
  },

  /**
   * POST /videos/mini-hooks/:id_video/image
   * Sobe a imagem/GIF de um mini-hook direto para o Bunny CDN e devolve a URL
   * pública. Frontend chama isso quando o usuário escolhe o arquivo, evitando
   * armazenar base64 no estado/localStorage.
   */
  uploadMiniHookImage: async (videoId: string, hookId: string, file: File): Promise<{ fileUrl: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('hookId', hookId);
    const response = await api.post(`/videos/mini-hooks/${videoId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  },

  /**
   * PATCH /videos/conversion-journey/:id_video
   * Salva a config da Jornada de Conversão (preroll/postroll) como JSON.
   */
  saveConversionJourney: async (videoId: string, conversionJourney: any): Promise<any> => {
    return await api.patch(`/videos/conversion-journey/${videoId}`, { conversionJourney });
  },

  /**
   * PATCH /videos/sales-notification/:id_video
   * Salva a config da Notificação de Vendas (prova social sobre o vídeo) como
   * JSON. O objeto vai inteiro — o backend faz `$set` parcial do documento.
   */
  saveSalesNotification: async (videoId: string, salesNotification: any): Promise<any> => {
    return await api.patch(`/videos/sales-notification/${videoId}`, { salesNotification });
  },

  /**
   * PATCH /videos/final-countdown/:id_video
   * Salva a config da Contagem Final Inteligente (card com contagem regressiva
   * exibido quando o vídeo termina) como JSON.
   */
  saveFinalCountdown: async (videoId: string, finalCountdown: any): Promise<any> => {
    return await api.patch(`/videos/final-countdown/${videoId}`, { finalCountdown });
  },

  /**
   * GET /journey/:idVideo/metrics?inicio&fim
   * Métricas agregadas da Jornada (entrada/saída) para a aba "Métricas".
   */
  getConversionJourneyMetrics: async (
    videoId: string,
    inicio?: string,
    fim?: string,
  ): Promise<any> => {
    const qs = inicio && fim ? `?inicio=${inicio}&fim=${fim}` : '';
    return await api.get(`/journey/${videoId}/metrics${qs}`);
  },

  /**
   * POST /videos/conversion-journey/:id_video/image
   * Sobe a imagem do CTA da Jornada (slot 'entry-cta' | 'exit-cta') pro Bunny e
   * devolve a URL pública, evitando base64 no estado/banco.
   */
  uploadConversionJourneyImage: async (
    videoId: string,
    slot: 'entry-cta' | 'exit-cta',
    file: File,
  ): Promise<{ fileUrl: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('slot', slot);
    const response = await api.post(`/videos/conversion-journey/${videoId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  },

  saveAllConfigurations: async (videoId: string, data: {
    // Autoplay
    cor?: string;
    corText?: string;
    textInferior?: string;
    textSuperior?: string;
    haveAutoplay?: boolean;
    typesAutoplay?: string;
    autoplayIconColor?: string;
    haveSmallTemplate?: boolean;
    havePulse?: boolean;
    haveRestart?: boolean;
    haveBorderWhite?: boolean;
    haveCustomImage?: boolean;
    customImage?: string;
    // Posição/tamanho custom do overlay de autoplay (templates resizáveis:
    // autoPlay/small/large/transparent). Valores em % do container do vídeo.
    // `null` = usuário não customizou (layout padrão do template).
    autoplayPositionXPercent?: number | null;
    autoplayPositionYPercent?: number | null;
    autoplayWidthPercent?: number | null;
    autoplayHeightPercent?: number | null;
    // Desbloqueia o áudio na primeira interação do visitante na página.
    autoplayDetectInteraction?: boolean;
    // Clique automático: o player tenta iniciar o vídeo com som sozinho, sem
    // esperar o visitante clicar no overlay do autoplay.
    autoplayAutoClick?: boolean;
    // Tela flutuante (Picture-in-Picture) do overlay de autoplay. Coluna
    // dedicada no backend — independente do PiP dos controles
    // (`havePictureInPicture`), pra não vazar o botão entre os dois recursos.
    autoplayPictureInPicture?: boolean;
    // Border
    haveBorder?: boolean;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    haveBorderRadius?: boolean;
    // Controls
    haveControls?: boolean;
    haveBigPlay?: boolean;
    haveProgresso?: boolean;
    haveSkip?: boolean;
    haveRewind?: boolean;
    haveVolume?: boolean;
    haveFullScreen?: boolean;
    havePlaybackSpeed?: boolean;
    havePictureInPicture?: boolean;
    haveTvCast?: boolean;
    haveStoryboard?: boolean;
    storyboardUrl?: string;
    haveBlockPause?: boolean;
    haveTempoAtual?: boolean;
    haveSmallPlay?: boolean;
    controlColor?: string;
    controlColorIcon?: string;
    controlBarEnabled?: boolean;
    controlBarColor?: string;
    controlsTheme?: string;
    customIconUrl?: string | null;
    // Progress Bar
    corBarra?: string;
    haveFakeBar?: boolean;
    fakeBarHeight?: number;
    // Ao Vivo (Audience)
    haveAoVivo?: boolean;
    backgroundAoVivo?: string;
    textAoVivo?: string;
    simulationAoVivo?: string;
    audienceSimulationSpeed?: string;
    fontColorAoVivo?: string;
    // Continue Watching
    haveContinue?: boolean;
    textContinue?: string;
    textMessage?: string;
    textRestart?: string;
    backgroundContinue?: string;
    continueTextColor?: string;
    continuePlayButtonBgColor?: string;
    continueRestartButtonBgColor?: string;
    typesContinue?: string;
    // Headline
    haveHeadline?: boolean;
    headlineText?: string;
    subHeadlineText?: string;
    headlineColor?: string;
    subHeadlineColor?: string;
    headlineSize?: string | number;
    subHeadlineSize?: string | number;
    headlineTypeFont?: string;
    headlineFontWeight?: string;
    isHTMLSelected?: boolean;
    headlineHtmlCode?: string;
    headlineInitialDelay?: string;
    headlineEndDelay?: string;
    headlineShowThroughout?: boolean;
    // Thumbnail Button
    haveButtonThumb?: boolean;
    selectedSize?: string;
    textButton?: string;
    textLink?: string;
    backgroundButton?: string;
    backgroundButtonHover?: string;
    textButtonColor?: string;
    textButtonColorHover?: string;
    selectedPosition?: string;
    havePause?: boolean;
    // Thumbnail de pausa: modo de exibição + tempo (persistidos no save geral)
    thumbPauseMode?: string;
    thumbPauseTimeStart?: string;
    thumbPauseTimeEnd?: string;
    // Video name
    nomeVideo?: string;
    // Affiliate (future)
    haveAffiliateLink?: boolean;
    urlAffiliate?: string;
    selectedAffiliateLogoPosition?: string;
    transparencyAffiliateLogo?: string;
    // Smart Pause
    haveSmartPause?: boolean;
    // Other
    noneControlsMoreAutoplay?: boolean;
  }): Promise<any> => {
    return await api.patch(`/videos/upload/${videoId}`, data);
  },

  saveVideoFeedback: async (videoId: string, data: {
    haveVideoFeedback: boolean;
    videoFeedbackConfig: any;
  }): Promise<any> => {
    return await api.patch(`/videos/video-feedback/${videoId}`, data);
  },
};

export default videoService;
